import { AddIcon } from '@chakra-ui/icons'
import { Box, Button, FormControl, FormLabel, Heading, HStack, Input, Radio, RadioGroup, Select, Text, Flex, Divider } from '@chakra-ui/react'

import '@mdxeditor/editor/style.css'
import { useEffect, useState } from 'react'
import { keyValue, keyValueObject, keyValuePair } from '../lib/types'
import { fileInterface } from '../components/MultiFileUpload/multiFileUpload.main'
import { getEnvironmentURL } from '../commons/env'

const formElementStyle = {
  bg: 'white'
}

export interface SelectOptionsInterface {
  value: string
  displayValue: string
}
export interface fileResult {
  id: string | number
  document_type: { id: string }
  document_url: string | null
  is_private_file: boolean
}

export const SnapText = ({
  defaultValue,
  title,
  type = 'text',
  callBack
}: {
  defaultValue?: string
  type?: 'text' | 'number'
  title: string
  callBack: (value: string) => void
}): JSX.Element => {
  return (
    <FormControl mb="4">
      <FormLabel fontWeight={'bold'} fontSize={'14px'}>
        {title}
      </FormLabel>
      <Input
        fontSize={'14px'}
        {...formElementStyle}
        type="text"
        key={`snapText_${title.replaceAll(' ', '')}`}
        defaultValue={defaultValue}
        onChange={event => {
          const HTMLElement = event.currentTarget as HTMLInputElement
          callBack(HTMLElement.value)
        }}
      />
    </FormControl>
  )
}
export const SnapSelectBox = ({
  options,
  defaultValue,
  title,
  callBack,
  chakraProps
}: {
  options: SelectOptionsInterface[]
  defaultValue: string | null
  title: string
  callBack: (value: { value: string }) => void
  chakraProps?: keyValuePair
}): JSX.Element => {
  return (
    <FormControl mb="4">
      <FormLabel fontWeight={'bold'} fontSize={'14px'}>
        {title}
      </FormLabel>
      <Select
        fontSize={'14px'}
        {...formElementStyle}
        {...chakraProps}
        key={`select_${title.replaceAll(' ', '')}`}
        onChange={event => {
          const HTMLElement = event.currentTarget as HTMLSelectElement
          callBack({
            value: HTMLElement.value
          })
        }}
        defaultValue={defaultValue ?? ''}
      >
        <option value="">-Select {title.toLowerCase()}-</option>
        {options.map(item => (
          <option key={`id_${item.value}_${item.displayValue}`} value={item.value}>
            {item.displayValue}
          </option>
        ))}
      </Select>
    </FormControl>
  )
}

export const SnapRadio = ({
  options,
  defaultValue,
  title,
  callBack
}: {
  options: SelectOptionsInterface[]
  defaultValue: string
  title: string
  callBack: (radioValue: { value: string }) => void
}): JSX.Element => {
  return (
    <FormControl mb="4">
      <>
        <FormLabel fontWeight={'bold'} fontSize={'14px'}>
          {title}
        </FormLabel>
        <RadioGroup
          onChange={value => {
            callBack({ value })
          }}
          mt="3"
          defaultValue={defaultValue}
        >
          <HStack direction="row" justifyContent={'flex-start'} ml="4">
            {options?.map(item => (
              <Radio mr="4" key={`${item.value}_${title.replaceAll(' ', '')}`} value={item.value}>
                <Text fontSize={'14px'}>{item.displayValue}</Text>
              </Radio>
            ))}
          </HStack>
        </RadioGroup>
      </>
    </FormControl>
  )
}
export const keyValueObjectToSelectOptionsArray = (keyValueObject: keyValue[], valueKey: string): SelectOptionsInterface[] => {
  if (!Array.isArray(keyValueObject)) {
    return [
      {
        value: '',
        displayValue: ''
      }
    ]
  }

  return keyValueObject.reduce(
    (reducer: SelectOptionsInterface[], item) => [
      ...reducer,
      {
        value: item.id as string,
        displayValue: item[valueKey] as string
      }
    ],
    []
  )
}
export const SystemFormHeading = ({ title }: { title: string }): JSX.Element => (
  <Heading size={'md'} pb="2" mb="6" mt="12" color={'purple.500'} borderBottom={'1px'} borderBottomColor="purple.300">
    {title}
  </Heading>
)
export const getSelectedValue = (value: { value: string }): { id: string } | string => (value.value ? { id: value.value } : '')
export const Seperator = ({ chakraProps }: { chakraProps?: keyValue }): JSX.Element => (
  <Box height={'1px'} my="6" bg="purple.200" {...chakraProps}></Box>
)

export const SegmentTabs = ({
  tabs,
  onChangeCallBack,
  defaultTabIndex = 0
}: {
  tabs: string[]
  onChangeCallBack: (tabIndex: number) => void
  defaultTabIndex: number
}): JSX.Element => {
  const [currentTab, setCurrentTab] = useState<number>(defaultTabIndex)

  return (
    <Box borderBottom="1px" borderColor="#ddd" mt="2">
      {tabs.map((item, index) => (
        <Button
          key={`tabs_${index}`}
          color={currentTab === index ? 'blue' : 'grey.400'}
          background={currentTab === index ? '#d9e2ff' : 'transparent'}
          borderBottomLeftRadius="0"
          borderBottomRightRadius="0"
          onClick={() => {
            setCurrentTab(index)
            onChangeCallBack(index)
          }}
        >
          {item}
        </Button>
      ))}
    </Box>
  )
}
export const MinMaxComponent = ({
  title,
  textBoxKey,
  radioKey,
  defaultValue,
  minTextDefaultValue,
  maxTextDefaultValue,
  onChangeCallBack
}: {
  title: string
  textBoxKey: string
  radioKey: string
  defaultValue: string
  minTextDefaultValue: string
  maxTextDefaultValue: string
  onChangeCallBack: (data: keyValueObject) => void
}): JSX.Element => {
  const [radioYes, setRadioYes] = useState<boolean>(defaultValue === 'true')
  const [maxValue, setMaxValue] = useState<string>(maxTextDefaultValue)
  const [minValue, setMinValue] = useState<string>(minTextDefaultValue)
  const [currentData, setCurrentData] = useState<keyValueObject>()
  useEffect(() => {
    onChangeCallBack(currentData as keyValueObject)
  }, [currentData, minValue, maxValue, radioYes])
  return (
    <>
      <SnapRadio
        title={title}
        defaultValue={defaultValue}
        options={[
          { value: 'true', displayValue: 'Y' },
          { value: 'false', displayValue: 'N' }
        ]}
        callBack={radioValue => {
          setCurrentData({
            [radioKey]: radioValue.value === 'true',
            [textBoxKey]: {
              min: minValue,
              max: maxValue
            }
          })
          setRadioYes(radioValue.value === 'true')
        }}
      />
      <HStack spacing={4} mt="1" px="4">
        {radioYes && (
          <>
            {[
              {
                title,
                key: textBoxKey,
                innerKey: 'min',
                defaultValue: minTextDefaultValue,
                keytoNotUpdate: 'max'
              },
              {
                title,
                key: textBoxKey,
                defaultValue: maxTextDefaultValue,
                innerKey: 'max',
                keytoNotUpdate: 'min'
              }
            ].map(item => {
              return (
                <Box key={`${item.key}${item.innerKey}`} width="100%">
                  <SnapText
                    title={item.innerKey === 'min' ? 'Min' : 'Max'}
                    defaultValue={item.innerKey === 'min' ? minValue : maxValue}
                    callBack={callBackValue => {
                      if (item.innerKey === 'min') {
                        setMinValue(callBackValue)
                      }

                      if (item.innerKey === 'max') {
                        setMaxValue(callBackValue)
                      }

                      setCurrentData({
                        [radioKey]: radioYes,
                        [textBoxKey]: {
                          min: item.innerKey === 'min' ? callBackValue : minValue,
                          max: item.innerKey === 'max' ? callBackValue : maxValue
                        }
                      })
                    }}
                  />
                </Box>
              )
            })}
          </>
        )}
      </HStack>
    </>
  )
}
export const stringToBoolean = (value: string): boolean => (value === 'true' || value === 'false' ? value === 'true' : false)
export const keysToRemove = (object: keyValue | null, keys: string[]): keyValue | null => {
  if (object !== null && typeof object === 'object') {
    return Object.keys(object)?.reduce((reducer: keyValue, key) => {
      if (!keys.includes(key)) {
        return { ...reducer, [key]: object[key] }
      }

      return reducer
    }, {})
  }
  return null
}

interface toastOptionsInterface {
  title: string
  description: string
  status: string
  duration: number
  isClosable: boolean
}

export const toastOptions: {
  successOptions: toastOptionsInterface
  failureOptions: (message: string) => toastOptionsInterface
} = {
  successOptions: {
    title: 'Success',
    description: 'Saved',
    status: 'success',
    duration: 4000,
    isClosable: true
  },
  failureOptions: (message: string) => ({
    title: 'Error',
    description: message ?? 'Failed',
    status: 'error',
    duration: 4000,
    isClosable: true
  })
}
export const formDataTemplate: keyValueObject = {
  system_code: '',
  system_description: '',
  multiple_services: false,
  no_of_services: '',
  application: '',
  fitting: false,
  fitting_allowed: null as unknown as string,
  display_record: false,
  fitting_type: {},
  frl_actual: '',
  frl_actual_ins: '',
  frl_actual_int: '',
  frl_actual_st: '',
  frl_issued: '',
  frl_issued_ins: '',
  frl_issued_int: '',
  frl_issued_st: '',
  industry_category: '',
  is_active: 'false',
  penetration_depth: '',
  penetration_depth_unit: '',
  // system_component_products: [
  //   {
  //     component_product: null,
  //     component_product_details: ''
  //   }
  // ],
  system_component_products: [],
  system_elements: [],
  system_notes: [],
  system_products: [],
  system_reports: [],
  // system_services: [
  //   {
  //     is_range: false,
  //     range_details: { min: '', max: '' },
  //     is_lagging: false,
  //     lagging_details: { min: '', max: '' },
  //     quantity: null,
  //     service: null,
  //     service_category: null,
  //     service_type: null
  //   }
  // ],
  system_services: [],
  system_docs: [] as fileInterface[]
}

interface serviceResultInterface {
  service: { id: string } | null
  service_category: { id: string } | null
  service_type: { id: string } | null
}

export const ExistingAndNewService = ({
  serviceSelectOptions,
  serviceCategorySelectOptions,
  serviceTypeSelectOptions,
  defaultServiceValue,
  defaultCategoryServiceValue,
  defaultServiceTypeValue,
  callBack
}: {
  serviceSelectOptions: SelectOptionsInterface[]
  serviceCategorySelectOptions: SelectOptionsInterface[]
  serviceTypeSelectOptions: SelectOptionsInterface[]
  defaultServiceValue: string
  defaultCategoryServiceValue: string
  defaultServiceTypeValue: string
  callBack: (data: serviceResultInterface) => void
}): JSX.Element => {
  const [result, setResult] = useState<serviceResultInterface>({
    service: defaultServiceValue ? { id: defaultServiceValue } : null,
    service_category: defaultCategoryServiceValue ? { id: defaultCategoryServiceValue } : null,
    service_type: defaultServiceTypeValue ? { id: defaultServiceTypeValue } : null
  })
  const [radioYes, setRadioYes] = useState<'true' | 'false'>(defaultServiceValue !== null && defaultServiceValue !== undefined ? 'true' : 'false')

  useEffect(() => {
    callBack(result)
  }, [result])

  return (
    <>
      <SnapRadio
        title={'Choose Existing or Create new service'}
        defaultValue={defaultServiceValue !== undefined && defaultServiceValue !== null ? 'true' : 'false'}
        options={[
          { value: 'true', displayValue: 'Existing Service' },
          { value: 'false', displayValue: 'New Service' }
        ]}
        callBack={radioValue => {
          setRadioYes(radioValue.value as 'true' | 'false')
        }}
      />
      {radioYes === 'true' && (
        <SnapSelectBox
          title={'Service'}
          defaultValue={defaultServiceValue}
          options={serviceSelectOptions}
          callBack={selectValue => {
            setResult({
              service: {
                id: selectValue.value
              },
              service_category: null,
              service_type: null
            })
          }}
        />
      )}
      {radioYes === 'false' && (
        <>
          <SnapSelectBox
            title={'Service Category'}
            defaultValue={defaultCategoryServiceValue}
            options={serviceCategorySelectOptions}
            callBack={selectValue => {
              setResult(rest => ({
                ...rest,
                service: null,
                service_category: {
                  id: selectValue.value
                }
              }))
            }}
          />

          <SnapSelectBox
            title={'Service Type'}
            defaultValue={defaultServiceTypeValue}
            options={serviceTypeSelectOptions}
            callBack={selectValue => {
              setResult(rest => ({
                ...rest,
                service: null,
                service_type: {
                  id: selectValue.value
                }
              }))
            }}
          />
        </>
      )}
    </>
  )
}

export const AddComponentButton = ({ title, onClickCallBack }: { title: string; onClickCallBack: () => void }): JSX.Element => (
  <Button ml="2" mt="2" pl="3" bg={'purple.700'} color="white" fontWeight="bold" fontSize={14} colorScheme={'purple'} onClick={onClickCallBack}>
    <AddIcon boxSize={3} mr="2" />
    {title}
  </Button>
)

export const filterObject = (object: keyValue[], key: string, value: string | boolean | null): keyValue[] | null => {
  // eslint-disable-next-line valid-typeof
  if (typeof object === null || typeof object === undefined || !object?.length) {
    return null
  }

  return object?.filter(item => item[key] === value) ?? null
}
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const uploadFile = async (formData: FormData) => {
  const uploadURL = getEnvironmentURL.fileUpload
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment
  const data = await fetch(uploadURL, {
    method: 'POST',
    body: formData
  })
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    .then(res => res.json())
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .then(data => data)
    .catch(() => {
      return null
    })

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return data
}
